//@ts-nocheck
import PassengerFieldsTable from '../PassengersFieldsTable/PassengersFieldsTable';
import PassengerFieldLibraryTable from '../PassengerFieldLibraryTable/PassengerFieldLibraryTable';
import PassengerCollectionPanel from '../PassengerCollectionPanel/PassengerCollectionPanel';
import PassengerFieldModal from '../PassengerFieldModal/PassengerFieldModal';
import PassengersFieldSkeleton from '../PassengersFieldSkeleton/PassengersFieldSkeleton';
import DeleteModal from '../../../Trip/DeleteModal/DeleteModal';
import PassengerFieldSlideout from '../../PassengerFieldSlideout/PassengerFieldSlideout';
import { Box } from '@mui/material';
import {
  getPassengerFields,
  createPassengerField,
  updatePassengerField,
  deletePassengerField,
  updatePassengerFieldSequence,
  updatePassengerFieldStatus
} from '../../../../services/tenant/tenant';
import { getFieldTypes } from '../../../../services/fieldtype/fieldtype';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Books, PlusCircle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { XGButton } from 'xg-cl';
import { ErrorToast, SuccessToast } from '../../../../utils/alerts';
import {
  buildPassengerFieldTypeOptions,
  filterPassengerFieldWaivers,
  filterPassengerFieldUploads,
  isFileUploadPassengerFieldType
} from '../../../../utils/passengerFieldTypes';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  REQUIRED,
  OPTIONAL,
  HIDDEN,
  INTERNAL
} from '../../../../constants/passengerFieldStatuses';
import './PassengerFieldsPanel.css';
import * as PropTypes from 'prop-types';

const initialFormData = {
  selectedFieldType: null,
  isEditing: false,
  title: '',
  description: '',
  isrequired: false,
  ishidden: false,
  isoptional: true,
  isinternal: false,
  passengerfieldoption: [],
  passengerfieldconfig: null,
  error: {
    title: '',
    description: ''
  }
};

const buildPassengerFieldRoundUsageMap = (fields = [], rounds = [], t) => {
  return (fields ?? []).reduce((usageMap, field) => {
    const usedInRounds = [];

    if (field?.isrequired || field?.isoptional) {
      usedInRounds.push(t('settings:atBooking'));
    }

    (rounds ?? []).forEach((round) => {
      const isFieldAssignedToRound = (round?.fields ?? []).some(
        (roundField) => roundField?.passengerfieldid === field?.passengerfieldid
      );

      if (!isFieldAssignedToRound) {
        return;
      }

      const roundLabel =
        round?.collectionanchortype === 'after_booking' &&
        round?.sendoffsetdays === 0
          ? t('settings:atBooking')
          : round?.name;

      if (roundLabel && !usedInRounds.includes(roundLabel)) {
        usedInRounds.push(roundLabel);
      }
    });

    usageMap.set(field?.passengerfieldid, usedInRounds);
    return usageMap;
  }, new Map());
};

const applyPassengerFieldRoundUsage = (
  fields = [],
  roundUsageMap = new Map()
) => {
  return (fields ?? []).map((field) => ({
    ...field,
    usedinrounds: roundUsageMap.get(field?.passengerfieldid) ?? []
  }));
};

const PassengerFieldsPanel = ({ id }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddAnswerDisabled, setIsAddAnswerDisabled] = useState(false);
  const [deletingPassengerField, setDeletingPassengerField] = useState(null);
  const [passengerFields, setPassengerFields] = useState([]);
  const passengerFieldRoundUsageMapRef = useRef(new Map());
  const [fieldTypes, setFieldTypes] = useState([]);
  const [activeFlagTab, setActiveFlagTab] = useState('field-library');
  const [openCreateRoundRequest] = useState(0);
  const [openCollectionLibraryRequest, setOpenCollectionLibraryRequest] =
    useState(0);
  const { t } = useTranslation(['passengers', 'common', 'errors', 'settings']);
  const {
    tripNavigation,
    itineraryNavigation,
    enableInternalPassengerFields,
    enablePax2ndRound,
    enablePassengerFieldUploads,
    enablePaxWaivers
  } = useFlags();
  const buildInitialForm = useCallback(
    () => ({
      ...initialFormData,
      isoptional: enablePax2ndRound ? false : initialFormData.isoptional,
      ishidden: enablePax2ndRound ? false : enableInternalPassengerFields,
      selectedFieldType: fieldTypes && fieldTypes?.length ? fieldTypes[0] : null
    }),
    [enableInternalPassengerFields, enablePax2ndRound, fieldTypes]
  );
  const [fieldForm, setFieldForm] = useState(buildInitialForm);
  const visiblePassengerFields = useMemo(
    () =>
      filterPassengerFieldWaivers(
        filterPassengerFieldUploads(
          passengerFields,
          enablePassengerFieldUploads
        ),
        enablePaxWaivers
      ),
    [enablePassengerFieldUploads, enablePaxWaivers, passengerFields]
  );

  const syncPassengerFieldRoundUsage = useCallback(
    (rounds = []) => {
      setPassengerFields((prevFields) => {
        const nextRoundUsageMap = buildPassengerFieldRoundUsageMap(
          prevFields,
          rounds,
          t
        );

        passengerFieldRoundUsageMapRef.current = nextRoundUsageMap;
        return applyPassengerFieldRoundUsage(prevFields, nextRoundUsageMap);
      });
    },
    [t]
  );

  const fetchPassengerFields = useCallback(async () => {
    try {
      const { data = [] } = await getPassengerFields();
      setPassengerFields(() => {
        const nextRoundUsageMap =
          passengerFieldRoundUsageMapRef.current.size > 0
            ? passengerFieldRoundUsageMapRef.current
            : buildPassengerFieldRoundUsageMap(data ?? [], [], t);

        if (passengerFieldRoundUsageMapRef.current.size === 0) {
          passengerFieldRoundUsageMapRef.current = nextRoundUsageMap;
        }

        return applyPassengerFieldRoundUsage(data ?? [], nextRoundUsageMap);
      });
    } catch (error) {
      setPassengerFields([]);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const fetchFieldTypes = useCallback(async () => {
    try {
      const { data = [] } = await getFieldTypes();

      if (data?.length > 0) {
        const fieldTypesList = buildPassengerFieldTypeOptions(data, {
          enablePassengerFieldUploads,
          t
        });

        setFieldTypes(fieldTypesList);
        setFieldForm((prev) => ({
          ...prev,
          selectedFieldType: fieldTypesList[0] ?? null
        }));
      }
    } catch (error) {
      setFieldTypes([]);
    }
  }, [enablePassengerFieldUploads, t]);

  const resetForm = () => {
    setFieldForm(buildInitialForm());
  };

  const resetErrors = () => {
    setFieldForm((prev) => ({
      ...prev,
      error: null
    }));
  };

  const isFormValid = () => {
    resetErrors();
    let isValid = true;

    const title = fieldForm?.title?.trim();
    const selectedFieldType = fieldForm?.selectedFieldType;
    const fieldOptions = fieldForm?.passengerfieldoption ?? [];

    if (!title) {
      setFieldForm((prev) => ({
        ...prev,
        error: { ...prev.error, title: t('fieldTitleRequired') }
      }));

      isValid = false;
    }

    if (!selectedFieldType) {
      setFieldForm((prev) => ({
        ...prev,
        error: {
          ...prev.error,
          selectedFieldType: t('fieldTypeRequired')
        }
      }));

      isValid = false;
    }

    if (selectedFieldType?.allowoptions && !fieldOptions?.length) {
      setFieldForm((prev) => ({
        ...prev,
        error: { ...prev.error, fieldOptions: t('fieldAnswersRequired') }
      }));

      isValid = false;
    }

    if (selectedFieldType?.allowoptions && fieldOptions?.length) {
      const newOptions = fieldOptions.map((option) => {
        const { title } = option;

        if (!title?.trim()) {
          isValid = false;
          return {
            ...option,
            error: t('answerTitleRequired')
          };
        }

        return option;
      });

      setFieldForm((prev) => ({
        ...prev,
        passengerfieldoption: newOptions
      }));
    }

    return isValid;
  };

  const onCloseModal = () => {
    resetForm();
    setIsAddAnswerDisabled(false);
    setIsOpenModal(false);
  };

  const onOpenModal = () => {
    resetForm();
    setIsAddAnswerDisabled(false);
    setIsOpenModal(true);
    setIsSubmitting(false);
  };

  const moveJustAddedFieldToTop = async (passengerfieldid) => {
    if (enablePax2ndRound) {
      return;
    }

    const customFields = passengerFields?.filter((field) => {
      return !field?.issystemfield;
    });

    const systemFields = passengerFields
      ?.filter((field) => {
        return field?.issystemfield;
      })
      .sort((a, b) => a?.sequence - b?.sequence);

    const justAddedFieldSequence =
      systemFields && systemFields?.length
        ? systemFields[systemFields?.length - 1]?.sequence + 1
        : 1;

    if (customFields && customFields?.length && passengerfieldid) {
      await updatePassengerFieldSequence(passengerfieldid, {
        sequence: justAddedFieldSequence
      });
    }
  };

  const onSavePassengerField = async () => {
    setIsSubmitting(true);

    try {
      const isFieldValid = isFormValid();

      if (!isFieldValid) {
        return;
      }

      const { isEditing = false, passengerfieldid = null } = fieldForm;
      const isFileUploadField = isFileUploadPassengerFieldType(
        fieldForm?.selectedFieldType
      );
      const fieldOptions =
        fieldForm?.passengerfieldoption?.map((option) => {
          const { title, sequence, isselected } = option;
          return { title, sequence, isselected };
        }) ?? [];

      const payload = {
        fieldtypeid: fieldForm?.selectedFieldType?.fieldtypeid ?? null,
        title: fieldForm?.title ?? '',
        description: fieldForm?.description ?? '',
        isrequired: fieldForm?.isrequired ?? false,
        ishidden: fieldForm?.ishidden ?? false,
        isoptional: fieldForm?.isoptional ?? false,
        isinternal: fieldForm?.isinternal ?? false,
        passengerfieldoption: fieldOptions ?? [],
        passengerfieldconfig: isFileUploadField
          ? null
          : (fieldForm?.passengerfieldconfig ?? null)
      };

      delete payload?.passengerfieldconfig?.passengerfieldconfigid;

      if (!isEditing) {
        const { data, error } = await createPassengerField(payload);

        if (!data || error) {
          ErrorToast(t('errors:passengerFieldCouldNotBeCreated'));
        } else {
          const { passengerfieldid } = data;

          await moveJustAddedFieldToTop(passengerfieldid);
          await fetchPassengerFields();
          setIsOpenModal(false);
          SuccessToast(t('passengerFieldCreatedSuccessfully'));
        }
      } else {
        const { data, error } = await updatePassengerField(
          payload,
          passengerfieldid
        );

        if (!data || error) {
          ErrorToast(t('errors:passengerFieldCouldNotBeUpdated'));
        } else {
          await fetchPassengerFields();
          setIsOpenModal(false);
          SuccessToast(t('passengerFieldUpdatedSuccessfully'));
        }
      }
    } catch (error) {
      if (fieldForm?.isEditing) {
        ErrorToast(t('errors:passengerFieldCouldNotBeUpdated'));
      } else {
        ErrorToast(t('errors:passengerFieldCouldNotBeCreated'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnEditField = (fieldId) => {
    const foundField = passengerFields.find(
      (field) => field.passengerfieldid === fieldId
    );

    if (foundField) {
      const {
        fieldtype,
        title = '',
        description = '',
        isrequired,
        ishidden,
        isoptional,
        isinternal,
        issystemfield = false,
        passengerfieldoption = [],
        passengerfieldconfig = null
      } = foundField;

      const foundFieldType = fieldTypes.find(
        (fieldType) => fieldType?.fieldtypeid === fieldtype?.fieldtypeid
      );

      const fieldData = {
        passengerfieldid: fieldId,
        selectedFieldType: foundFieldType ?? null,
        isEditing: true,
        title,
        description,
        isrequired,
        ishidden,
        isoptional,
        isinternal,
        issystemfield,
        passengerfieldoption,
        passengerfieldconfig,
        error: {}
      };

      setFieldForm(fieldData);
      setIsOpenModal(true);
      setIsSubmitting(false);
      setIsAddAnswerDisabled(foundFieldType?.key === 'boolean');
    }
  };

  const handleOnDeleteField = (field) => {
    setIsDeleteModalOpen(true);
    setDeletingPassengerField(field);
  };

  const getDeletePassengerFieldDescription = () => {
    const usedInRounds =
      deletingPassengerField?.usedinrounds?.filter(Boolean) ?? [];

    if (!usedInRounds.length) {
      return `${t('passengerFieldDeletionConfirmation')} ${t(
        'common:thisActionCannotBeUndone'
      )}`;
    }

    return `${t('passengerFieldDeletionAssignedRounds', {
      rounds: usedInRounds.join(', ')
    })} ${t('common:thisActionCannotBeUndone')}`;
  };

  const handleDeletePassengerFieldConfirmation = async () => {
    try {
      setIsSubmitting(true);
      const { data, error } = await deletePassengerField(
        deletingPassengerField?.passengerfieldid
      );

      if (!data || error) {
        ErrorToast(t('errors:passengerFieldCouldNotBeDeleted'));
      } else {
        await fetchPassengerFields();
        SuccessToast(t('passengerFieldDeletedSuccessfully'));
      }
    } catch (error) {
      ErrorToast(t('errors:passengerFieldCouldNotBeDeleted'));
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingPassengerField(null);
      setIsSubmitting(false);
    }
  };

  const revertPassengerFieldOrder = (
    sourcePassengerFieldId = 0,
    destinationPassengerFieldId = 0
  ) => {
    const newPassengerFields = [...passengerFields];

    const sourcePassengerFieldIndex = passengerFields.findIndex(
      (field) => field.passengerfieldid === sourcePassengerFieldId
    );

    const destinationPassengerFieldIndex = passengerFields.findIndex(
      (field) => field.passengerfieldid === destinationPassengerFieldId
    );

    const tempField = newPassengerFields[sourcePassengerFieldIndex];
    newPassengerFields[sourcePassengerFieldIndex] =
      newPassengerFields[destinationPassengerFieldIndex];
    newPassengerFields[destinationPassengerFieldIndex] = tempField;

    setPassengerFields(newPassengerFields);
  };

  const revertPassengerFieldStatus = (originalPassengerField = null) => {
    const { isrequired, isoptional, ishidden, isinternal, passengerfieldid } =
      originalPassengerField;
    const newPassengerFields = [...passengerFields];

    const index = newPassengerFields.findIndex(
      (field) => field?.passengerfieldid === passengerfieldid
    );

    if (index !== -1) {
      const updatedField = {
        ...newPassengerFields[index],
        isrequired,
        isoptional,
        ishidden,
        isinternal
      };
      newPassengerFields[index] = updatedField;
      setPassengerFields(newPassengerFields);
    }
  };

  const handleUpdateSequence = async (
    sourcePassenger,
    destinationPassenger
  ) => {
    const { passengerfieldid: sourcePassengerFieldId } = sourcePassenger;
    const { passengerfieldid: destinationPassengerFieldId, sequence = 0 } =
      destinationPassenger;

    try {
      setIsSubmitting(true);
      const { data, error } = await updatePassengerFieldSequence(
        sourcePassengerFieldId,
        { sequence }
      );

      if (!data || error) {
        ErrorToast(t('errors:passengerFieldSequenceCouldNotBeUpdated'));

        revertPassengerFieldOrder(
          sourcePassengerFieldId,
          destinationPassengerFieldId
        );
      } else {
        await fetchPassengerFields();
      }
    } catch (error) {
      ErrorToast(t('errors:passengerFieldSequenceCouldNotBeUpdated'));
      revertPassengerFieldOrder(
        sourcePassengerFieldId,
        destinationPassengerFieldId
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldStatusChange = async (fieldObject, value) => {
    try {
      const { passengerfieldid } = fieldObject;

      const newPassengerFields = [...passengerFields];
      const index = newPassengerFields.findIndex(
        (field) => field?.passengerfieldid === passengerfieldid
      );

      if (index !== -1) {
        const updatedField = {
          ...newPassengerFields[index],
          isrequired: value === REQUIRED,
          isoptional: value === OPTIONAL,
          ishidden: !enableInternalPassengerFields
            ? value === HIDDEN
            : newPassengerFields[index]?.ishidden,
          isinternal: value === INTERNAL
        };
        newPassengerFields[index] = updatedField;
        setPassengerFields(newPassengerFields);

        setIsSubmitting(true);

        const { data, error } = await updatePassengerFieldStatus(
          passengerfieldid,
          {
            isrequired: updatedField.isrequired,
            isoptional: updatedField.isoptional,
            ishidden: updatedField.ishidden,
            isinternal: updatedField.isinternal
          }
        );

        if (!data || error) {
          ErrorToast(t('errors:passengerFieldStatusCouldNotBeUpdated'));
          revertPassengerFieldStatus(fieldObject);
        } else {
          setFieldForm((prev) => ({
            ...prev,
            isrequired: data?.isrequired,
            isoptional: data?.isoptional,
            isinternal: data?.isinternal
          }));
        }
      }
    } catch (error) {
      ErrorToast(t('errors:passengerFieldStatusCouldNotBeUpdated'));
      revertPassengerFieldStatus(fieldObject);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnCheckBoxChange = async (passengerfieldid, payload) => {
    try {
      const newPassengerFields = [...passengerFields];
      const index = newPassengerFields.findIndex(
        (field) => field?.passengerfieldid === passengerfieldid
      );

      if (index !== -1) {
        const updatedField = {
          ...newPassengerFields[index],
          ...payload
        };
        newPassengerFields[index] = updatedField;
        setPassengerFields(newPassengerFields);

        setIsSubmitting(true);

        const { data, error } = await updatePassengerFieldStatus(
          passengerfieldid,
          {
            isrequired: updatedField.isrequired,
            isoptional: updatedField.isoptional,
            ishidden: updatedField.ishidden,
            isinternal: updatedField.isinternal
          }
        );

        if (!data || error) {
          ErrorToast(t('errors:passengerFieldCouldNotBeUpdated'));
        } else {
          setFieldForm((prev) => ({
            ...prev,
            ishidden: data.ishidden
          }));
        }
      }
    } catch (error) {
      ErrorToast(t('errors:passengerFieldCouldNotBeUpdated'));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchPassengerFields();
    fetchFieldTypes();
  }, [fetchFieldTypes, fetchPassengerFields]);

  const legacyContent = (
    <>
      <Box
        style={{ paddingBottom: '24px' }}
        className={`global-passenger-fields-actions ${tripNavigation ? 'global-passenger-fields-actions--fixed' : ''}`}
      >
        <XGButton
          autoFocus
          id='button-add-amenity'
          text={t('addCustomField')}
          type='primary'
          size='medium'
          variant='contained'
          onClick={onOpenModal}
          startIcon={<PlusCircle />}
        />
      </Box>
      <PassengerFieldsTable
        passengerFields={visiblePassengerFields}
        handleOnEditField={handleOnEditField}
        handleOnDeleteField={handleOnDeleteField}
        handleUpdateSequence={handleUpdateSequence}
        handleUpdateStatus={handleFieldStatusChange}
        isDropDisabled={isSubmitting}
        isSubmitting={isSubmitting}
        handleOnCheckBoxChange={handleOnCheckBoxChange}
      />
    </>
  );

  return (
    <Box
      id={id}
      className={`trip__passenger-fields-panel ${tripNavigation ? 'trip__passenger-fields-panel--fixed' : ''}`}
      data-testid='global-passenger-fields-panel'
    >
      {isLoading ? (
        <PassengersFieldSkeleton id='passenger-field-skeleton' />
      ) : (
        <>
          {enablePax2ndRound ? (
            <Box className='passenger-fields-panel__tabs-wrapper'>
              <Box className='passenger-fields-panel__flag-tabs'>
                <Box className='passenger-fields-panel__flag-tabs-header'>
                  <Box className='passenger-fields-panel__flag-tabs-nav'>
                    <button
                      type='button'
                      className={`passenger-fields-panel__flag-tab ${activeFlagTab === 'field-library' ? 'passenger-fields-panel__flag-tab--active' : ''}`}
                      onClick={() => setActiveFlagTab('field-library')}
                    >
                      {t('settings:fieldLibrary')}
                    </button>
                    <button
                      type='button'
                      className={`passenger-fields-panel__flag-tab ${activeFlagTab === 'collection' ? 'passenger-fields-panel__flag-tab--active' : ''}`}
                      onClick={() => setActiveFlagTab('collection')}
                    >
                      {t('settings:collection')}
                    </button>
                  </Box>

                  {activeFlagTab === 'field-library' && (
                    <XGButton
                      id='button-add-passenger-field-library'
                      text={t('passengers:addCustomField')}
                      type='secondary'
                      size='medium'
                      variant='outlined'
                      onClick={onOpenModal}
                      startIcon={<PlusCircle />}
                    />
                  )}

                  {activeFlagTab === 'collection' && (
                    <XGButton
                      id='button-add-passenger-field-from-library'
                      text={t('common:addFromLibrary')}
                      type='secondary'
                      size='medium'
                      variant='outlined'
                      onClick={() =>
                        setOpenCollectionLibraryRequest((prev) => prev + 1)
                      }
                      startIcon={<Books size={18} />}
                    />
                  )}
                </Box>

                {activeFlagTab === 'field-library' ? (
                  <Box className='passenger-fields-panel__tab-content'>
                    <PassengerFieldLibraryTable
                      passengerFields={visiblePassengerFields}
                      handleOnEditField={handleOnEditField}
                      handleOnDeleteField={handleOnDeleteField}
                      headerAction={<></>}
                      isSubmitting={isSubmitting}
                    />
                  </Box>
                ) : (
                  <Box className='passenger-fields-panel__tab-content'>
                    <PassengerCollectionPanel
                      passengerFields={visiblePassengerFields}
                      openCreateRoundRequest={openCreateRoundRequest}
                      openLibraryDrawerRequest={openCollectionLibraryRequest}
                      headerActions={<></>}
                      refreshPassengerFields={fetchPassengerFields}
                      syncPassengerFieldRoundUsage={
                        syncPassengerFieldRoundUsage
                      }
                      handleOnEditField={handleOnEditField}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            legacyContent
          )}

          {!itineraryNavigation && (
            <PassengerFieldModal
              fieldTypes={fieldTypes}
              fieldForm={fieldForm}
              setFieldForm={setFieldForm}
              isOpen={isOpenModal}
              isSubmitting={isSubmitting}
              isAddAnswerDisabled={isAddAnswerDisabled}
              setIsAddAnswerDisabled={setIsAddAnswerDisabled}
              onCancel={onCloseModal}
              onSubmit={onSavePassengerField}
              isGlobalSettings={true}
            />
          )}

          <PassengerFieldSlideout
            id={`trip-passenger-field-slideout`}
            isOpen={isOpenModal && itineraryNavigation}
            onClose={onCloseModal}
            onSave={onSavePassengerField}
            isSubmitting={isSubmitting}
            isAddAnswerDisabled={isAddAnswerDisabled}
            setIsAddAnswerDisabled={setIsAddAnswerDisabled}
            fieldTypes={fieldTypes}
            fieldForm={fieldForm}
            setFieldForm={setFieldForm}
            isGlobalSettings={true}
          />

          <DeleteModal
            id='delete-passenger-field-component'
            isOpenModal={isDeleteModalOpen}
            title={t('deletePassengerField')}
            onConfirm={handleDeletePassengerFieldConfirmation}
            onCancel={() => {
              setIsDeleteModalOpen(false);
            }}
            description={getDeletePassengerFieldDescription()}
            isSubmitting={isSubmitting}
          />
        </>
      )}
    </Box>
  );
};

PassengerFieldsPanel.propTypes = {
  id: PropTypes.string
};

export default PassengerFieldsPanel;
